import React, { useEffect, useMemo, useState } from "react";
import { TurfTalk } from "@/api/client"; // eslint-disable-line no-unused-vars
import { Sports } from "@/api/sports";
import { AuthExpiredError } from "@/api/client";
import { PageHeader, Skeleton } from "@/components/ui-kit";
import { DataState, SourceNote } from "@/components/DataState";
import { SportsNav, LeagueToggle, SeasonSelect } from "@/components/SportsControls";
import { CURRENT_SEASON, formatRecord } from "@/lib/sports";

// Tolerant row accessors — the standings schema is assumed until the backend ships it.
const val = (r, keys, dflt = "—") => { for (const k of keys) if (r?.[k] != null) return r[k]; return dflt; };
function rowRecord(r, prefix) {
  const w = r?.[`${prefix}_wins`] ?? r?.[`${prefix}_w`];
  const l = r?.[`${prefix}_losses`] ?? r?.[`${prefix}_l`];
  if (w == null && l == null) return r?.[`${prefix}_record`] || "—";
  return formatRecord({ w, l, t: r?.[`${prefix}_ties`] ?? r?.[`${prefix}_t`] });
}

const COLS = [
  { key: "team", label: "Team" }, { key: "overall", label: "W-L-T" }, { key: "pct", label: "PCT" },
  { key: "conf", label: "CONF" }, { key: "div", label: "DIV" },
  { key: "pf", label: "PF" }, { key: "pa", label: "PA" }, { key: "diff", label: "DIFF" },
  { key: "strk", label: "STRK" }, { key: "seed", label: "SEED" },
];

function cellFor(key, r) {
  switch (key) {
    case "team": return r.team_name || r.name || r.abbr || "—";
    case "overall": return formatRecord({ w: val(r, ["wins", "w"], 0), l: val(r, ["losses", "l"], 0), t: r.ties ?? r.t }) || "—";
    case "pct": return val(r, ["pct", "win_pct"]);
    case "conf": return rowRecord(r, "conference") || rowRecord(r, "conf");
    case "div": return rowRecord(r, "division") || rowRecord(r, "div");
    case "pf": return val(r, ["pf", "points_for"]);
    case "pa": return val(r, ["pa", "points_against"]);
    case "diff": return val(r, ["diff", "point_differential", "differential"]);
    case "strk": return val(r, ["streak", "strk"]);
    case "seed": return val(r, ["seed", "playoff_seed"]);
    default: return "—";
  }
}

function StandingsGroup({ group }) {
  const title = [group.conference, group.division].filter(Boolean).join(" ");
  const teams = group.teams || [];
  return (
    <section className="mb-8" data-testid={`standings-group-${title.replace(/\s+/g, "-") || "group"}`}>
      {title && <div className="font-display tracking-widest text-xs text-white/40 mb-2">{title}</div>}
      {/* Desktop / tablet table — horizontally contained, never overflows the page */}
      <div className="hidden sm:block overflow-x-auto tt-scroll rounded-xl border border-[#2A3544]">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-white/40 text-[11px] font-display tracking-wide">
            {COLS.map((c) => <th key={c.key} className={`px-3 py-2 ${c.key === "team" ? "text-left" : "text-center"}`}>{c.label}</th>)}
          </tr></thead>
          <tbody>
            {teams.map((r, i) => (
              <tr key={r.team_id || r.id || i} className="border-t border-[#2A3544]" data-testid={`standings-row-${r.team_id || r.id || i}`}>
                {COLS.map((c) => <td key={c.key} className={`px-3 py-2 tabnums ${c.key === "team" ? "text-left text-white" : "text-center text-white/70"}`}>{cellFor(c.key, r)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile compact cards — no wide tables on phones */}
      <div className="sm:hidden space-y-2">
        {teams.map((r, i) => (
          <div key={r.team_id || r.id || i} className="tt-panel rounded-lg p-3" data-testid={`standings-card-${r.team_id || r.id || i}`}>
            <div className="flex items-center justify-between">
              <span className="text-white text-sm">{cellFor("team", r)}</span>
              <span className="text-white/70 text-sm tabnums">{cellFor("overall", r)}{cellFor("seed", r) !== "—" ? ` · #${cellFor("seed", r)}` : ""}</span>
            </div>
            <div className="mt-1 text-[11px] text-white/45 tabnums flex flex-wrap gap-x-3">
              <span>CONF {cellFor("conf", r)}</span><span>DIV {cellFor("div", r)}</span>
              <span>PF {cellFor("pf", r)}</span><span>PA {cellFor("pa", r)}</span><span>DIFF {cellFor("diff", r)}</span><span>STRK {cellFor("strk", r)}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function StandingsPage() {
  const [league, setLeague] = useState("NFL");
  const [season, setSeason] = useState(CURRENT_SEASON);
  const [res, setRes] = useState(null);
  const [ranks, setRanks] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Sports.standings(league, season)
      .then(setRes)
      .catch((e) => { if (!(e instanceof AuthExpiredError)) setRes({ state: "error", groups: [] }); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [league, season]);
  // NCAAF rankings — real endpoint; renders only when the feed returns a poll.
  useEffect(() => {
    if (league !== "NCAA") { setRanks(null); return; }
    Sports.rankings(league, season).then(setRanks).catch(() => setRanks({ state: "error", teams: [] }));
  }, [league, season]);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Standings" subtitle="Conference & division standings from the live provider feed." right={<SeasonSelect value={season} onChange={setSeason} />} />
      <SportsNav />
      <div className="mb-6"><LeagueToggle value={league} onChange={setLeague} /></div>

      {loading ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : res?.state === "ok" ? (
        <>
          {res.groups.map((g, i) => <StandingsGroup key={i} group={g} />)}
          <SourceNote timestamp={res.source_timestamp} coverage={res.coverage || "Live provider feed"} />
        </>
      ) : (
        <DataState
          state={res?.state || "error"}
          onRetry={load}
          title={res?.state === "feature_unavailable" ? "Standings are being connected" : undefined}
          detail={res?.state === "feature_unavailable" ? `${league} standings will appear here as soon as the live feed is connected.` : undefined}
        />
      )}

      {league === "NCAA" && ranks && (
        <section className="mt-8" data-testid="ncaa-rankings">
          <h2 className="font-display tracking-wide text-lg text-white mb-3">Rankings{ranks.poll ? ` · ${ranks.poll}` : ""}</h2>
          {ranks.state === "ok" ? (
            <div className="space-y-1.5">
              {ranks.teams.map((r, i) => (
                <div key={r.team_id || r.id || i} className="flex items-center gap-3 tt-panel rounded-lg px-3 py-2" data-testid={`ranking-row-${r.rank ?? i + 1}`}>
                  <span className="w-6 text-center font-display text-[#4ea152]">{r.rank ?? i + 1}</span>
                  <span className="text-sm text-white">{r.team_name || r.name || r.abbr}</span>
                  {r.trend && r.trend !== "NR" && <span className="text-[10px] text-white/40">{r.trend}</span>}
                  {r.record != null && <span className="ml-auto text-xs text-white/50 tabnums">{typeof r.record === "string" ? r.record : formatRecord(r.record)}</span>}
                </div>
              ))}
            </div>
          ) : <DataState state={ranks.state} title="Rankings not available yet" detail="AP / Coaches poll rankings will appear when the provider feed exposes them." />}
        </section>
      )}
    </div>
  );
}
